import React from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useDistributionSummaryCards } from "../../hooks/useDistributionSummaryCards";
import StudentDistributionSummaryCard, {
  type StudentDistributionSummaryCardData,
} from "./StudentDistributionSummaryCard";
import { EmptyState } from "./EmptyState";

const TITLE_TO_ICON: Record<
  string,
  { iconType: string; iconColor: string; subtitleColor: string }
> = {
  "Current Presence": {
    iconType: "presence",
    iconColor: "bg-orange-500",
    subtitleColor: "text-gray-500 dark:text-[#B0B0B0]",
  },
  "Peak Today": {
    iconType: "peak",
    iconColor: "bg-blue-500",
    subtitleColor: "text-emerald-500 dark:text-emerald-400",
  },
  "Avg Dwell Time": {
    iconType: "avgDwell",
    iconColor: "bg-purple-500",
    subtitleColor: "text-gray-500 dark:text-[#B0B0B0]",
  },
  "Peak Dwell Time": {
    iconType: "peakDwell",
    iconColor: "bg-green-500",
    subtitleColor: "text-gray-500 dark:text-[#B0B0B0]",
  },
};

function mapApiCardToCardData(item: {
  id?: string;
  title: string;
  value: string;
  subtitle: string;
  timestamp?: string;
  locations?: { name: string; value: number }[];
}): StudentDistributionSummaryCardData {
  const icon =
    TITLE_TO_ICON[item.title] ?? {
      iconType: "presence",
      iconColor: "bg-blue-500",
      subtitleColor: "text-gray-500 dark:text-[#B0B0B0]",
    };
  return {
    title: item.title,
    value: item.value,
    subtitle: item.subtitle,
    peakTimestamp: item.timestamp,
    subtitleColor: icon.subtitleColor,
    iconType: icon.iconType,
    iconColor: icon.iconColor,
    locations: item.locations,
  };
}

const CARD_CLASS_DARK =
  "bg-[#1C202F]/55 border border-slate-700/70 text-[#F9FAFB] shadow-none rounded-2xl";
const CARD_CLASS_LIGHT =
  "bg-white/95 border border-gray-200 text-[#111827] shadow-none rounded-2xl";

const SECTION_TITLE_CLASS = "font-bold tracking-tight";
const SECTION_SUBTITLE_CLASS = "text-sm text-slate-400";

const TITLE_TEXT_DARK = "text-slate-50";
const TITLE_TEXT_LIGHT = "text-slate-900";
const SUBTITLE_TEXT_LIGHT = "text-slate-600";

export interface FilterParams {
  location_id?: string;
  camera_id?: string;
  from?: string;
  to?: string;
  timezone?: string;
}

export const StudentDistributionSummaryCards: React.FC<{
  filterParams?: FilterParams;
  distributionType?: string;
  roleFilter?: "student" | "staff";
  /** When false, no summary REST/WebSocket calls. */
  apisEnabled?: boolean;
}> = ({ filterParams, distributionType, roleFilter, apisEnabled = true }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const cardClass = isDarkMode ? CARD_CLASS_DARK : CARD_CLASS_LIGHT;

  const fetchEnabled = apisEnabled !== false;
  const selectedType = distributionType ?? roleFilter ?? "student";

  const { data: rawCards = [], isLoading, isError } = useDistributionSummaryCards({
    from: filterParams?.from,
    to: filterParams?.to,
    location_id: filterParams?.location_id,
    camera_id: filterParams?.camera_id,
    type: selectedType,
    enabled: fetchEnabled,
  });

  const cards = rawCards.map(mapApiCardToCardData);
  const serviceUnavailable = fetchEnabled && !isLoading && !isError && cards.length === 0;

  if (!fetchEnabled) {
    return (
      <div className={`${cardClass} p-4 sm:p-6 rounded-2xl shadow-none mb-5 sm:mb-8`}>
        <EmptyState
          variant="people"
          title="Summary API not wired"
          description="Turn on summary API fetching or connect your new endpoints when ready."
          isDarkMode={isDarkMode}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`${cardClass} p-4 sm:p-6 rounded-2xl shadow-none mb-5 sm:mb-8`}>
        <div className="flex items-center gap-2.5 sm:gap-3 mb-5 sm:mb-6">
          <div className={`p-2 rounded-xl ${isDarkMode ? "bg-blue-500/20" : "bg-blue-500/10"}`}>
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3
              className={`text-base sm:text-xl ${SECTION_TITLE_CLASS} ${
                isDarkMode ? TITLE_TEXT_DARK : TITLE_TEXT_LIGHT
              }`}
            >
              Key Metrics Overview
            </h3>
            <p className={SECTION_SUBTITLE_CLASS + ` ${isDarkMode ? "" : SUBTITLE_TEXT_LIGHT}`}>
              Real-time statistics and performance indicators
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-32 rounded-xl animate-pulse ${
                isDarkMode ? "bg-slate-700/50" : "bg-slate-200"
              }`}
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`${cardClass} p-4 sm:p-6 rounded-2xl shadow-none mb-5 sm:mb-8`}>
        <p className="text-red-500 dark:text-red-400 text-sm">
          Failed to load {selectedType} summary.
        </p>
      </div>
    );
  }

  if (serviceUnavailable) {
    return (
      <div className={`${cardClass} p-4 sm:p-6 rounded-2xl shadow-none mb-5 sm:mb-8`}>
        <EmptyState
          variant="people"
          title="No presence data yet"
          description="No camera data is available for the selected filters. Try a different location or date."
          isDarkMode={isDarkMode}
        />
      </div>
    );
  }

  if (selectedType === "student") {
    return (
      <div className={`${cardClass} p-3 sm:p-4 rounded-2xl mb-2`}>
        <div className="flex items-center gap-2.5 sm:gap-3 mb-4">
          <div
            className={`p-2 rounded-xl ${
              isDarkMode ? "bg-blue-500/20" : "bg-blue-500/10"
            } border border-blue-500/20`}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3
              className={`text-base sm:text-xl ${SECTION_TITLE_CLASS} ${
                isDarkMode ? TITLE_TEXT_DARK : TITLE_TEXT_LIGHT
              }`}
            >
              Key Metrics Overview
            </h3>
            <p className={`text-xs ${isDarkMode ? "text-slate-400" : SUBTITLE_TEXT_LIGHT}`}>
              Real-time statistics and performance indicators
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          {cards.map((card, index) => (
            <StudentDistributionSummaryCard
              key={`student-${card.title ?? index}`}
              data={card}
              isDarkMode={isDarkMode}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`${cardClass} p-3 sm:p-4 rounded-2xl shadow-none`}>
      <div className="flex items-center gap-2.5 sm:gap-3 mb-4">
        <div
          className={`p-2 rounded-xl ${
            isDarkMode ? "bg-teal-500/20" : "bg-teal-500/10"
          } border border-teal-500/20`}
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <h3
            className={`text-base sm:text-xl ${SECTION_TITLE_CLASS} ${
              isDarkMode ? TITLE_TEXT_DARK : TITLE_TEXT_LIGHT
            }`}
          >
            Key Metrics Overview
          </h3>
          <p className={`text-xs ${isDarkMode ? "text-slate-400" : SUBTITLE_TEXT_LIGHT}`}>
            Real-time statistics and performance indicators
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((card, index) => (
          <StudentDistributionSummaryCard
            key={`staff-${card.title ?? index}`}
            data={card}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>
    </div>
  );
};

export default StudentDistributionSummaryCards;
